/**
 * Camera-control command catalogue + capability cache.
 *
 * Commands are AGENTS.md §10 territory (highest-risk surface). The
 * operator UI sends commands over the bus; the field side handles them
 * on the LiveKit local track. Capabilities are broadcast by the field
 * once on track-ready so the operator can grey out controls that aren't
 * supported on the device.
 *
 * Hard rule from §10: **never silently fail a command**. Field handlers
 * always ack with `{ok}` or nack with a `reason` so the operator UI can
 * surface "torch not supported on this device" and the operator can fall
 * back to verbal guidance.
 */
import { create } from "zustand";


export type FacingMode = "user" | "environment";


export type SwitchCameraPayload = {
  /** "toggle" inverts current; explicit "user" / "environment" jumps. */
  facing: FacingMode | "toggle";
};

export type SwitchCameraAck = {
  ok: boolean;
  facing?: FacingMode;
  reason?: string;
};


export type TogglePayload = { on: boolean | "toggle" };
export type ToggleAck = {
  ok: boolean;
  on?: boolean;
  reason?: string;
};


/** Zoom level the operator wants applied. Value lives in the same
 * absolute coordinate space the device reports — see ZoomRange. */
export type ZoomPayload = { level: number };
export type ZoomAck = {
  ok: boolean;
  level?: number;
  reason?: string;
};


/** Zoom capability shape. Browsers expose `MediaTrackCapabilities.zoom`
 * as `{min, max, step}` on supporting devices (mostly back cameras on
 * Android Chromium). Most iPhones return zoom range 1.0-1.0 → no zoom.
 */
export type ZoomRange = {
  min: number;
  max: number;
  step?: number;
  /** Current zoom level at broadcast time (from getSettings). */
  current?: number;
};


/** Capabilities the operator UI cares about right now. The shape stays
 * narrow because we only surface what we use — anything else lives in
 * the raw MediaTrackCapabilities the field sends along. */
export type Capabilities = {
  /** Number of distinct cameras the field has access to. */
  camera_count: number;
  /** Whether the active back camera supports torch. */
  torch: boolean;
  /** Zoom capability — null means unsupported on this device. Operator
   * UI shows the slider only when min < max. */
  zoom: ZoomRange | null;
  /** Active facing mode at broadcast time. */
  facing?: FacingMode;
  /** Raw capabilities snapshot for debugging / forward compatibility. */
  raw?: MediaTrackCapabilities;
};


type Store = {
  capabilities: Capabilities | null;
  set: (c: Capabilities) => void;
  clear: () => void;
};


export const useCapabilitiesStore = create<Store>((set) => ({
  capabilities: null,
  set: (c) => set({ capabilities: c }),
  clear: () => set({ capabilities: null }),
}));


/* --------------- helpers --------------- */


/** Read a sensible Capabilities snapshot from a MediaStreamTrack. */
export async function probeCapabilities(
  track: MediaStreamTrack,
): Promise<Capabilities> {
  let cameraCount = 1;
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.();
    if (devices) {
      cameraCount = devices.filter((d) => d.kind === "videoinput").length;
    }
  } catch {
    /* enumerateDevices may reject without permission — fall back to 1 */
  }
  const raw = track.getCapabilities ? track.getCapabilities() : ({} as MediaTrackCapabilities);
  const settings = track.getSettings ? track.getSettings() : ({} as MediaTrackSettings);
  return {
    camera_count: cameraCount,
    torch: !!(raw as MediaTrackCapabilities & { torch?: boolean }).torch,
    zoom: _readZoomRange(raw, settings),
    facing: (settings.facingMode as FacingMode | undefined) ?? undefined,
    raw,
  };
}


/** Extract the zoom range from a MediaTrackCapabilities + Settings pair.
 * Returns null when zoom is unsupported OR when min/max are equal (the
 * "zoom is technically reported but useless" case most iPhones hit). */
function _readZoomRange(
  caps: MediaTrackCapabilities,
  settings: MediaTrackSettings,
): ZoomRange | null {
  const zoomCap = (caps as MediaTrackCapabilities & {
    zoom?: { min?: number; max?: number; step?: number };
  }).zoom;
  if (!zoomCap || typeof zoomCap.min !== "number" || typeof zoomCap.max !== "number") {
    return null;
  }
  if (zoomCap.max <= zoomCap.min) {
    // 1.0-1.0 range — slider would be a no-op.
    return null;
  }
  const current = (settings as MediaTrackSettings & { zoom?: number }).zoom;
  return {
    min: zoomCap.min,
    max: zoomCap.max,
    step: typeof zoomCap.step === "number" ? zoomCap.step : undefined,
    current: typeof current === "number" ? current : undefined,
  };
}


/** Apply the next facing mode to a LiveKit local video track and return
 * the facing actually achieved.
 *
 * Strategy:
 *   1. Try `mediaStreamTrack.applyConstraints({facingMode})` first.
 *      This swaps the camera on the SAME MediaStreamTrack — LiveKit's
 *      publication, the RTCPeerConnection sender, and the egress
 *      subscriber all see continuous frames. No black gap in the
 *      recorded MP4. Works on most modern Chromium-based browsers.
 *
 *   2. If applyConstraints rejects OR returns a facingMode that
 *      doesn't match (some browsers silently ignore the constraint),
 *      fall back to LiveKit's `restartTrack({facingMode})`. That
 *      releases the old MediaStreamTrack and acquires a new one —
 *      cleanly switches the camera on every browser but momentarily
 *      hands the egress a track swap (causes ~1-2s of black in the
 *      recorded MP4 while egress re-subscribes).
 *
 * LiveKit's `restartTrack(VideoCaptureOptions)` forwards a soft
 * preference to `getUserMedia({facingMode})` — there's no
 * `{exact: …}` escape hatch. On a single-camera device the browser
 * silently re-acquires the same camera; the operator UI uses
 * `presence/capabilities.camera_count` to disable the switch button
 * upfront so we don't get there.
 */
export async function switchFacing(
  track: {
    restartTrack: (c: { facingMode: FacingMode }) => Promise<void>;
    mediaStreamTrack?: MediaStreamTrack;
  },
  next: FacingMode,
): Promise<FacingMode> {
  const mst = track.mediaStreamTrack;
  if (mst && typeof mst.applyConstraints === "function") {
    try {
      await mst.applyConstraints({ facingMode: next });
      const got = (mst.getSettings?.().facingMode as FacingMode | undefined);
      if (got === next) {
        // Camera actually switched — no track replacement, no egress gap.
        return got;
      }
      // applyConstraints "succeeded" but the browser didn't flip
      // (e.g. some Android Chromium builds quietly ignore facingMode).
      // Fall through to restartTrack.
    } catch {
      // applyConstraints rejects (e.g. constraint not supported).
      // Fall through to restartTrack.
    }
  }
  await track.restartTrack({ facingMode: next });
  const settings = track.mediaStreamTrack?.getSettings?.();
  return ((settings?.facingMode as FacingMode | undefined) ?? next) as FacingMode;
}


/** Re-apply HD capture resolution to a LiveKit local video track.
 *
 * `switchFacing`'s applyConstraints path sends ONLY `{facingMode}`, which —
 * per the MDN spec — REPLACES the entire constraint set, dropping width/height
 * so the camera falls back to its default (480×640 on iPhone). Anything that
 * restarts / re-acquires the track (facing switch, app resume, LiveKit
 * restart) needs this afterwards to restore 1080p (720p fallback).
 *
 * The current facingMode is re-included so a subsequent
 * `applyConstraints({facingMode})` atomically REPLACES all constraints rather
 * than accumulating them (the 2026-05-17 iPhone lesson). No-ops when the track
 * is already ≥1280px in either orientation, so round-trip switches are cheap.
 */
export async function upgradeCaptureResolution(
  track: { mediaStreamTrack?: MediaStreamTrack } | null | undefined,
): Promise<void> {
  const mst = track?.mediaStreamTrack;
  if (!mst || typeof mst.applyConstraints !== "function") return;
  const before = mst.getSettings?.() ?? {};
  const facing = (before as { facingMode?: string }).facingMode as
    | FacingMode
    | undefined;
  const w = (before as { width?: number }).width ?? 0;
  const h = (before as { height?: number }).height ?? 0;
  // Already HD-ish (portrait iPhone lands at 1080×1920) → skip renegotiation.
  if (w >= 1280 || h >= 1280) return;
  const base: MediaTrackConstraints = facing ? { facingMode: facing } : {};
  // eslint-disable-next-line no-console
  console.info("[camera] upgrading %s from %dx%d → 1080p", facing ?? "?", w, h);
  try {
    await mst.applyConstraints({
      ...base,
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30 },
    });
  } catch {
    try {
      await mst.applyConstraints({
        ...base,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      });
    } catch {
      /* give up — the next capabilities broadcast surfaces the real size */
    }
  }
}


/** Backlog 2026-09-18 — the two capture-resolution tiers `applyQualityTier`
 * switches between as the field device's `ConnectionQualityChanged` signal
 * moves. "high" matches the room's normal publish default (see
 * livekitOptions.ts); "low" is a real cut (roughly 1/4 the pixels) for a
 * connection that's visibly struggling. */
const QUALITY_TIER_RESOLUTIONS = {
  high: { width: 1920, height: 1080, frameRate: 30 },
  low: { width: 640, height: 360, frameRate: 15 },
} as const;

export type CaptureQualityTier = keyof typeof QUALITY_TIER_RESOLUTIONS;

/** Re-apply a capture-resolution TIER to a LiveKit local video track.
 *
 * Uses `mediaStreamTrack.applyConstraints` — same mechanism as
 * `upgradeCaptureResolution` above, NOT `restartTrack`. applyConstraints
 * re-negotiates the encoder's send resolution on the SAME track (no
 * replacement, no renegotiation, no black gap for the egress/recording);
 * `restartTrack` tears down and re-acquires the camera, which is overkill
 * for a resolution-only change and exactly what upgradeCaptureResolution's
 * own docstring warns costs ~1-2s of black in the recording.
 *
 * The current facingMode is preserved for the same reason documented on
 * `upgradeCaptureResolution`: a bare `{width, height}` constraint set
 * REPLACES the whole constraint set per spec, which would silently drop
 * back to the front camera on some browsers if facingMode weren't
 * re-included.
 */
export async function applyQualityTier(
  track: { mediaStreamTrack?: MediaStreamTrack } | null | undefined,
  tier: CaptureQualityTier,
): Promise<void> {
  const mst = track?.mediaStreamTrack;
  if (!mst || typeof mst.applyConstraints !== "function") return;
  const before = mst.getSettings?.() ?? {};
  const facing = (before as { facingMode?: string }).facingMode as
    | FacingMode
    | undefined;
  const base: MediaTrackConstraints = facing ? { facingMode: facing } : {};
  const target = QUALITY_TIER_RESOLUTIONS[tier];
  try {
    await mst.applyConstraints({
      ...base,
      width: { ideal: target.width },
      height: { ideal: target.height },
      frameRate: { ideal: target.frameRate },
    });
  } catch {
    /* best-effort — a rejected constraint just leaves the current
       resolution in place; the next quality-change event tries again */
  }
}


/** Apply torch state. Throws with a readable message if not supported. */
export async function applyTorch(
  track: MediaStreamTrack,
  on: boolean,
): Promise<void> {
  const caps = track.getCapabilities ? track.getCapabilities() : ({} as MediaTrackCapabilities);
  if (!(caps as MediaTrackCapabilities & { torch?: boolean }).torch) {
    throw new Error("torch not supported on this device");
  }
  // The advanced clause is the way to ask for non-standard constraints.
  await track.applyConstraints({
    advanced: [{ torch: on } as MediaTrackConstraintSet],
  });
}


/** Apply a zoom level. Clamps to the device's reported range so the
 * operator can't push a value the camera will reject. Throws with a
 * readable message when zoom isn't supported (most iPhones, all
 * front-only desktops, etc.) so the operator UI surfaces the nack and
 * can fall back to verbal guidance.
 *
 * Returns the level actually applied (post-clamp + post-step
 * normalisation).
 */
export async function applyZoom(
  track: MediaStreamTrack,
  level: number,
): Promise<number> {
  const caps = track.getCapabilities ? track.getCapabilities() : ({} as MediaTrackCapabilities);
  const settings = track.getSettings ? track.getSettings() : ({} as MediaTrackSettings);
  const range = _readZoomRange(caps, settings);
  if (range === null) {
    throw new Error("zoom not supported on this device");
  }
  // Clamp to the device's range. Step normalisation is left to the
  // browser — most implementations round to the nearest step internally
  // and the actual applied value reads back via getSettings.
  const clamped = Math.min(range.max, Math.max(range.min, level));
  await track.applyConstraints({
    advanced: [{ zoom: clamped } as MediaTrackConstraintSet],
  });
  const after = (track.getSettings?.() as MediaTrackSettings & { zoom?: number }).zoom;
  return typeof after === "number" ? after : clamped;
}